import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Drawer,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@mui/material';

// Import Timeline components from @mui/lab
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import InfoIcon from '@mui/icons-material/Info';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StorageIcon from '@mui/icons-material/Storage';

import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import html2pdf from 'html2pdf.js';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import GetAppIcon from '@mui/icons-material/GetApp';
import { ErrorToast } from '../../components/Toasts';
import { GetShipmentDetails, InvoiceAuditLogs } from '../../apis/Agency';

export default function ViewInvoiceDetails(props) {
  const rowData = props?.rowData;

  const [shipmentDetailsArr, setShipmentDetailsArr] = useState([]);
  const [fetchItemsLoader, setFetchItemsLoader] = useState(false);
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState([]);
  const [allFields, setAllFields] = useState([]);
  const [selectedRevisions, setSelectedRevisions] = useState([]);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    field: 'revision', // Default sort by revision
    direction: 'asc' // 'asc' or 'desc'
  });

  const handleFetchLineItems = () => {
    setShipmentDetailsArr([]);
    setFetchItemsLoader(true);
    GetShipmentDetails(rowData.shipmentId)
      .then(res => {
        if (res.status === 200) {
          setShipmentDetailsArr(res.data.data);
          setFetchItemsLoader(false);
        }
      })
      .catch(err =>
        ErrorToast(`Get Shipment Details API failed due to ${err?.message}`)
      );
  };

  const shipmentTotals = shipmentDetailsArr?.reduce(
    (acc, shipment) => {
      return {
        totalWeight: acc.totalWeight + (Number(shipment.grossWeight) || 0),
        totalPieces: acc.totalPieces + (Number(shipment.pieces) || 0),
        totalUnits: acc.totalUnits + (Number(shipment.units) || 0),
        classes: [...acc.classes, shipment.freightClass].filter(Boolean)
      };
    },
    { totalWeight: 0, totalPieces: 0, totalUnits: 0, classes: [] }
  );

  // Get unique classes
  const uniqueClasses = [...new Set(shipmentTotals?.classes || [])];

  // Fetch on component mount or when rowData.shipmentId changes
  useEffect(() => {
    if (rowData?.shipmentId) {
      handleFetchLineItems();
    }
  }, [rowData?.shipmentId]);

  const carrierBreakdownKeys =
    rowData?.iaData && Object.keys(JSON.parse(rowData?.iaData));
  const iaData = rowData.iaData && JSON.parse(rowData.iaData);

  const [valueY, setValueY] = useState(0);
  const [valueX, setValueX] = useState(0);
  const label = { inputProps: { 'aria-label': 'Checkbox demo' } };
  const accessorialDescriptions = [
    'Inside Pickup',
    'Inside Delivery',
    'Lift Gate Pickup',
    'Lift Gate Delivery',
    'Residential Pickup',
    'Residential Delivery',
    'Secure Access Pickup',
    'Secure Access Delivery',
    'Limited Access Pickup',
    'Limited Access Delivery',
    'Freeze Protection',
    'Overlength',
    'Hazardous Material',
    'Additional Charges',
    'Declared Value',
    'Call Before Delivery',
    'COD Fee',
    'Miscellaneous Charges'
  ];

  const baseRateDescrptions = [
    'Line Haul',
    'Fuel Surcharge Value',
    'Guaranteed Charges'
  ];

  const carrierBreakdownList = [
    'NetCharges',
    'NETCHG',
    'FUEL CHARGES',
    'ACCESSORIAL CHG 01',
    'Net Charge',
    'NET CHARGES',
    'GROSS CHARGES',
    'FUEL SURCHARGE',
    'Net Revenue',
    'Total Accsrl',
    'NETCHARGES',
    'GrossCharge',
    'GROSSCHARGES',
    'Gross Charges',
    'Gross Charge',
    'Base Discount',
    'BaseDiscount',
    'OtherAccessorials',
    'TotalCharge',
    'TotalAccsrl',
    'NetRevenue',
    'FSC',
    'DiscountAmount',
    'DISCOUNT$',
    'CHARGES$',
    'FreightCharges',
    'FUELSURCHARGE',
    'FUELCHARGES',
    'FuelSurcharge',
    'Accessorials',
    'Extra1',
    'Extra2',
    'Extra3',
    'Extra4',
    'Extra5',
    'Extra6',
    'Extra7',
    'Extra8',
    'Extra9',
    'Extra10'
  ];

  // Get all non-empty keys that aren't in the breakdown lists
  let extraRows = Object.entries(iaData || {})
    .filter(([key, value]) => {
      // Skip if value is empty or zero
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        value === '$-' ||
        value === 0 ||
        value === '0' ||
        Number(value) === 0
      ) {
        return false;
      }

      // SPECIAL CASE: For Extra keys, always show them if they have values
      if (key.startsWith('Extra')) {
        return true;
      }

      // For non-Extra keys, skip if in carrierBreakdownList
      if (carrierBreakdownList.includes(key)) {
        return false;
      }

      // Skip common charge keys (case insensitive)
      const ignoreList = [
        'Net Revenue',
        'netcharges',
        'netcharge',
        'Accessorials',
        'Accessorial',
        'accessorials',
        'TotalCharge',
        'Gross Charge',
        'Base Discount',
        'FSC',
        'Net Charge',
        'GrossCharge',
        'GrossCharges',
        'OtherAccessorials',
        'TotalAccsrl',
        'NetRevenue',
        'DiscountAmount',
        'DISCOUNT$',
        'CHARGES$',
        'FreightCharges',
        'FUELSURCHARGE',
        'FUELCHARGES',
        'FuelSurcharge',
        'CODAmount',
        'ProNumber',
        'PaymentType',
        'PRO Number',
        'Pro #',
        'PRO#',
        'PR',
        'PRONumber',
        'Pro',
        'BOLNumber',
        'BOL Number',
        'BOL #',
        'BL',
        'BOL#',
        'BolNumber',
        'BOL',
        'TrackingNumber',
        'Tracking Number',
        'BillofLading',
        'Tracking #',
        'Tracking#',
        'PONumber',
        'PO Number',
        'PO #',
        'PO#',
        'Reference1',
        'Reference2',
        'Reference Number',
        'Ref #',
        'Ref#'
      ];

      const shouldIgnore = ignoreList.some(ignoreKey =>
        key.toLowerCase().includes(ignoreKey.toLowerCase())
      );

      return !shouldIgnore;
    })
    .map(([key, value]) => ({
      label: key,
      value
    }));

  function isAlmostEqual(a, b, epsilon = 0.0001) {
    const numA = Number(a);
    const numB = Number(b);
    if (isNaN(numA) || isNaN(numB)) return false;
    return Math.abs(numA - numB) < epsilon;
  }

  const applySubChargesSort = (rowA, rowB) => {
    return rowA.order - rowB.order;
  };

  const formatePrice = price => {
    const strPrice = String(price).trim();
    const num = Number(price);

    if (
      strPrice === 'N' ||
      strPrice === 'Y' ||
      strPrice === 'NaN' ||
      isNaN(num)
    ) {
      return '$0.00';
    }

    return num.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const mainCharges = rowData?.surchargesList
    ?.sort(applySubChargesSort)
    .filter(
      s =>
        !accessorialDescriptions.includes(s.description) &&
        s.description !== 'Surcharge'
    );

  const accessorialCharges = rowData?.surchargesList
    ?.sort(applySubChargesSort)
    .filter(s => accessorialDescriptions.includes(s.description));

  const carrierFilteredBreakdown = carrierBreakdownKeys
    ?.sort(applySubChargesSort)
    .filter(
      item =>
        carrierBreakdownList.includes(item) &&
        ![
          'Net Revenue',
          'netcharges',
          'netcharge',
          'Accessorials',
          'Accessorial',
          'accessorials',
          'TotalCharge'
        ].includes(item.toLowerCase())
    );

  const netChargeEntry = Object.entries(iaData || {}).find(([key]) =>
    rowData?.carrierName === 'R and L Carriers'
      ? key.replace(/\s+/g, '').toLowerCase() === 'totalcharge'
      : key.replace(/\s+/g, '').toUpperCase() === 'NETCHG' ||
        key.replace(/\s+/g, '').toUpperCase() === 'NETCHARGES' ||
        key.replace(/\s+/g, '').toLowerCase() === 'netrevenue' ||
        key.replace(/\s+/g, '').toLowerCase() === 'netcharge'
  );

  const netChargeObject = netChargeEntry ? netChargeEntry : {};

  const carrierAccessorials = Object.entries(iaData || {}).find(
    ([key]) =>
      key.replace(/\s+/g, '').toLowerCase() === 'accessorials' ||
      key.replace(/\s+/g, '').toUpperCase() === 'ACCESSORIALCHG01' ||
      key.replace(/\s+/g, '').toLowerCase() === 'otheraccessorials' ||
      key.replace(/\s+/g, '').toLowerCase() === 'totalaccsrl'
  );

  const carrierAccessorialsEntry = carrierAccessorials
    ? { [carrierAccessorials[0]]: carrierAccessorials[1] }
    : {};

  const tableRef = useRef(null);
  const comparisonTableRef = useRef(null);

  const handleGeneratePDF = () => {
    const element = tableRef.current;
    if (element) {
      html2pdf()
        .set({
          margin: 2,
          filename: 'Charges-breakdown.pdf',
          image: { type: 'jpeg', quality: 2 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        })
        .from(element)
        .save();
    }
  };

  // Add function to export comparison table as PDF
  const handleExportComparisonPDF = () => {
    const element = comparisonTableRef.current;
    if (element) {
      html2pdf()
        .set({
          margin: 0.5,
          filename: `Invoice-Revision-Comparison-${rowData?.shipmentId || 'export'}.pdf`,
          image: { type: 'jpeg', quality: 2 },
          html2canvas: { scale: 2, letterRendering: true },
          jsPDF: {
            unit: 'in',
            format: 'letter',
            orientation: 'landscape'
          }
        })
        .from(element)
        .save();
    }
  };

  // Updated function to export comparison table as CSV (now revisions as rows)
  const handleExportComparisonCSV = () => {
    // Prepare headers - first column is Revision, then all field names
    const headers = ['Revision', 'Date', 'Status', ...allFields];

    // Prepare rows - one row per selected revision
    const rows = selectedRevisions.map(revision => {
      const log = auditLogs.find(l => l.revisionNumber === revision);
      const rowData = [
        `Revision ${revision}`,
        log ? formatDate(log.createdOn) : '',
        log ? log.iaStatus : ''
      ];

      // Add values for each field
      allFields.forEach(field => {
        const fieldData = comparisonData.find(row => row.field === field);
        const cellData = fieldData ? fieldData[`revision_${revision}`] : null;
        rowData.push(cellData?.value || '-');
      });

      return rowData;
    });

    // Convert to CSV
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `Invoice-Revision-Comparison-${rowData?.shipmentId || 'export'}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const fetchAuditLogs = () => {
    setLogsLoading(true);
    InvoiceAuditLogs(rowData.shipmentId)
      .then(res => {
        if (res.status === 200) {
          // Sort logs by revision number in ASCENDING order (oldest first)
          const sortedLogs = res.data.data.sort(
            (a, b) => a.revisionNumber - b.revisionNumber
          );
          setAuditLogs(sortedLogs);

          // Process data for comparison table
          processComparisonData(sortedLogs);

          setLogsDrawerOpen(true);
        }
      })
      .catch(err =>
        ErrorToast(`Invoice Audit Logs API failed due to ${err?.message}`)
      )
      .finally(() => setLogsLoading(false));
  };

  // Process audit logs into comparison table format
  const processComparisonData = logs => {
    if (!logs || logs.length === 0) return;

    // Parse all iaData
    const parsedLogs = logs.map(log => ({
      ...log,
      parsedData: JSON.parse(log.iaData)
    }));

    // Get all unique field names from all revisions
    const allFieldSet = new Set();
    parsedLogs.forEach(log => {
      Object.keys(log.parsedData).forEach(key => {
        // Filter out empty or zero values
        const value = log.parsedData[key];
        if (
          value !== null &&
          value !== undefined &&
          value !== '' &&
          value !== '$-' &&
          value !== 0 &&
          value !== '0' &&
          Number(value) !== 0
        ) {
          allFieldSet.add(key);
        }
      });
    });

    const fields = Array.from(allFieldSet).sort();
    setAllFields(fields);

    // Create comparison data structure (still field-based for internal use)
    const comparison = fields.map(field => {
      const row = { field };

      // Add values for each revision
      parsedLogs.forEach(log => {
        const value = log.parsedData[field];
        row[`revision_${log.revisionNumber}`] = {
          value: value !== undefined ? String(value) : '-',
          date: log.createdOn,
          status: log.iaStatus
        };
      });

      return row;
    });

    setComparisonData(comparison);

    // Set all revisions as selected by default
    setSelectedRevisions(
      parsedLogs.map(log => log.revisionNumber).sort((a, b) => a - b)
    );
  };

  // Handle sort request
  const handleSort = field => {
    setSortConfig(prevConfig => ({
      field,
      direction:
        prevConfig.field === field && prevConfig.direction === 'asc'
          ? 'desc'
          : 'asc'
    }));
  };

  // Get sorted revisions
  const sortedRevisions = useMemo(() => {
    if (!selectedRevisions.length) return [];

    const revisionsWithData = selectedRevisions.map(revision => {
      const log = auditLogs.find(l => l.revisionNumber === revision);

      // Build a row object for sorting
      const row = {
        revision,
        date: log ? new Date(log.createdOn) : null,
        status: log ? log.iaStatus : '',
        values: {}
      };

      // Add field values for sorting
      allFields.forEach(field => {
        const fieldData = comparisonData.find(row => row.field === field);
        const cellData = fieldData ? fieldData[`revision_${revision}`] : null;
        row.values[field] = cellData?.value || '-';
      });

      return row;
    });

    // Sort based on current sort config
    return revisionsWithData
      .sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.field === 'revision') {
          aValue = a.revision;
          bValue = b.revision;
        } else if (sortConfig.field === 'date') {
          aValue = a.date;
          bValue = b.date;
        } else if (sortConfig.field === 'status') {
          aValue = a.status;
          bValue = b.status;
        } else {
          // Sort by a specific field value
          aValue = a.values[sortConfig.field];
          bValue = b.values[sortConfig.field];
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        // Compare values
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      })
      .map(item => item.revision);
  }, [selectedRevisions, allFields, comparisonData, auditLogs, sortConfig]);

  const formatDate = dateString => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
      // hour: '2-digit',
      // minute: '2-digit'
    });
  };

  const getStatusColor = status => {
    switch (status?.toUpperCase()) {
      case 'RESOLVED':
        return 'success';
      case 'DISPUTED':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = status => {
    const iconStyle = { fontSize: '1.2rem' };

    switch (status?.toUpperCase()) {
      case 'RESOLVED':
        return <CheckCircleIcon sx={{ color: '#4caf50', ...iconStyle }} />;
      case 'DISPUTED':
        return <ErrorIcon sx={{ color: '#f44336', ...iconStyle }} />;
      case 'PENDING':
        return <AccessTimeIcon sx={{ color: '#ff9800', ...iconStyle }} />;
      default:
        return <InfoIcon sx={{ color: '#9e9e9e', ...iconStyle }} />;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        borderRadius: '5px',
        p: 1,
        height: 'auto'
      }}
    >
      <div ref={tableRef} style={{ width: '100%' }}>
        <TableContainer
          elevation={0}
          sx={{
            width: '86vw',
            height: '50vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            border: '1px solid #ccc',
            backgroundColor: '#fff',
            boxShadow: 2
          }}
          component={Paper}
        >
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              bgcolor: '#f5f5f5',
              borderRadius: 1
            }}
          >
            {/* Shipment Details - Center aligned */}
            <Box sx={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
              {!fetchItemsLoader && shipmentDetailsArr?.length > 0 ? (
                <Typography
                  variant='body2'
                  sx={{
                    display: 'flex',
                    gap: 3,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}
                >
                  <span>
                    <strong>Total Gross Weight:</strong>{' '}
                    {shipmentTotals?.totalWeight?.toFixed(2)} lbs
                  </span>
                  <span>
                    <strong>Pieces:</strong> {shipmentTotals?.totalPieces}
                  </span>
                  <span>
                    <strong>Total Units:</strong> {shipmentTotals?.totalUnits}
                  </span>
                  <Chip
                    size='small'
                    label={`${shipmentDetailsArr.length} items`}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Typography>
              ) : (
                <Typography variant='body2' color='textSecondary'>
                  {fetchItemsLoader ? 'Loading...' : 'No shipment details'}
                </Typography>
              )}
            </Box>

            {/* View History Button - Right aligned */}
            <Button
              variant='outlined'
              size='small'
              onClick={fetchAuditLogs}
              disabled={logsLoading}
              startIcon={
                logsLoading ? <CircularProgress size={16} /> : <HistoryIcon />
              }
              sx={{
                minWidth: 100,
                height: 32,
                fontSize: '0.75rem'
              }}
            >
              {logsLoading ? 'Loading...' : 'View History'}
            </Button>
          </Box>

          <Table
            sx={{
              width: '86vw',
              '& td, & th': {
                border: '1px solid #e0e0e0'
              }
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#f7f7f7'
                  }
                }}
              >
                <TableCell sx={{ fontWeight: 'bold' }}>Charge Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>TMS Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Charge Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Invoice Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  Breakdown Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requiredDescriptions.map((desc, index) => {
                const main = mainCharges?.find(
                  item => item.description.trim() === desc
                ) || {
                  description: desc,
                  amount: { amount: '' }
                };
                let overrideKey = '';
                let overrideValue = '';
                let breakDownMatched = false;

                if (desc === 'Base Rate') {
                  if (
                    main.amount?.amount == iaData?.CHARGES$ ||
                    main.amount?.amount == iaData?.GROSSCHARGES ||
                    main.amount?.amount == iaData?.['GROSS CHARGES'] ||
                    main.amount?.amount == iaData?.['Gross Charges'] ||
                    main.amount?.amount == iaData?.['Gross Charge'] ||
                    main.amount?.amount == iaData?.GrossCharge ||
                    main.amount?.amount == iaData?.GrossCharges
                  ) {
                    breakDownMatched = true;
                  }

                  overrideKey = carrierFilteredBreakdown?.find(item =>
                    [
                      'CHARGES$',
                      'GROSSCHARGES',
                      'GROSS CHARGES',
                      'Gross Charges',
                      'Gross Charge',
                      'GrossCharge',
                      'GrossCharges'
                    ].includes(item)
                  );

                  overrideValue =
                    iaData?.CHARGES$ ||
                    iaData?.['GROSS CHARGES'] ||
                    iaData?.['Gross Charge'] ||
                    iaData?.['Gross Charges'] ||
                    iaData?.GROSSCHARGES ||
                    iaData?.GrossCharge ||
                    iaData?.GrossCharges;
                } else if (desc === 'Discount Value') {
                  if (
                    Math.abs(main.amount?.amount) ==
                      Math.abs(iaData?.DiscountAmount) ||
                    Math.abs(main.amount?.amount) ==
                      Math.abs(iaData?.DISCOUNT$) ||
                    Math.abs(main.amount?.amount) ==
                      Math.abs(iaData?.BaseDiscount) ||
                    Math.abs(main.amount?.amount) ==
                      Math.abs(iaData?.['Base Discount'])
                  ) {
                    breakDownMatched = true;
                  }

                  overrideKey = carrierFilteredBreakdown?.find(item =>
                    [
                      'DiscountAmount',
                      'DISCOUNT$',
                      'BaseDiscount',
                      'Base Discount'
                    ].includes(item)
                  );

                  overrideValue =
                    iaData?.DiscountAmount ||
                    iaData?.DISCOUNT$ ||
                    iaData?.BaseDiscount ||
                    iaData?.['Base Discount'];
                } else if (desc === 'Fuel Surcharge Value') {
                  if (
                    main.amount?.amount == iaData?.FuelSurcharge ||
                    main.amount?.amount == iaData?.FUELSURCHARGE ||
                    main.amount?.amount == iaData?.['FUEL SURCHARGE'] ||
                    main.amount?.amount == iaData?.['FUEL CHARGES'] ||
                    main.amount?.amount == iaData?.FUELCHARGES ||
                    main.amount?.amount == iaData?.FSC
                  ) {
                    breakDownMatched = true;
                  }

                  overrideKey = carrierFilteredBreakdown?.find(item =>
                    [
                      'FuelSurcharge',
                      'FUELSURCHARGE',
                      'FUEL SURCHARGE',
                      'FUEL CHARGES',
                      'FUELCHARGES',
                      'FSC'
                    ].includes(item)
                  );

                  overrideValue =
                    iaData?.FuelSurcharge ||
                    iaData?.FUELCHARGES ||
                    iaData?.FSC ||
                    iaData?.['FUEL CHARGES'] ||
                    iaData?.['FUEL SURCHARGE'] ||
                    iaData?.FUELSURCHARGE;
                }

                return (
                  <TableRow
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f7f7f7'
                      }
                    }}
                    key={index}
                  >
                    <TableCell align='left'>
                      {main?.description || ''}
                    </TableCell>

                    <TableCell align='left'>
                      {main?.description?.includes('%')
                        ? main?.amount?.amount
                          ? `${Math.abs(main?.amount?.amount).toFixed(2)}%`
                          : '_'
                        : main?.amount?.amount
                          ? formatePrice(Math.abs(main?.amount?.amount))
                          : '_'}
                    </TableCell>

                    <TableCell
                      align='left'
                      sx={{
                        color:
                          overrideKey &&
                          formatePrice(Math.abs(overrideValue)) !== '_'
                            ? breakDownMatched
                              ? 'green'
                              : 'red'
                            : 'black'
                      }}
                    >
                      {overrideKey || '_'}
                    </TableCell>

                    <TableCell
                      align='left'
                      sx={{
                        color:
                          overrideKey &&
                          formatePrice(Math.abs(overrideValue)) !== '_'
                            ? breakDownMatched
                              ? 'green'
                              : 'red'
                            : 'black'
                      }}
                    >
                      {overrideKey && overrideValue !== ''
                        ? formatePrice(Math.abs(overrideValue))
                        : '_'}
                    </TableCell>

                    <TableCell align='left'>
                      {overrideKey &&
                        formatePrice(Math.abs(overrideValue)) !== '_' && (
                          <Typography
                            sx={{
                              color: breakDownMatched ? 'green' : 'red',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            {breakDownMatched ? '✅ Matched' : '❌ Not Matched'}
                          </Typography>
                        )}
                    </TableCell>
                  </TableRow>
                );
              })}

              {/* Total Base Charge Row */}
              <TableRow
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 1,
                  '&:last-child td, &:last-child th': {
                    border: 0
                  },
                  '&:hover': {
                    backgroundColor: '#f7f7f7'
                  }
                }}
              >
                <TableCell sx={{ fontWeight: 'bold' }}>
                  Total Base Charge
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  $
                  {rowData?.surchargesList
                    ?.sort(applySubChargesSort)
                    .filter(
                      s =>
                        baseRateDescrptions.includes(s?.description) &&
                        s.description !== 'Surcharge'
                    )
                    .reduce((total, s) => total + (s?.amount?.amount || 0), 0)
                    .toFixed(2)}
                </TableCell>
                {rowData?.surchargesList?.some(
                  s => !accessorialDescriptions?.includes(s?.description)
                ) ? (
                  <>
                    <TableCell sx={{ fontWeight: 'bold' }}>_</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>_</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>_</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </>
                )}
              </TableRow>

              {/* Accessorials Row */}
              {(rowData?.surchargesList?.some(s =>
                accessorialDescriptions?.includes(s.description)
              ) ||
                (Object.keys(carrierAccessorialsEntry).length !== 0 &&
                  String(carrierAccessorials?.[1]).trim() &&
                  !['$-', 'null'].includes(
                    String(carrierAccessorials?.[1]).trim()
                  ))) && (
                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': {
                      border: 1
                    },
                    '&:hover': {
                      backgroundColor: '#f7f7f7'
                    }
                  }}
                >
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    Accessorials
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    $
                    {rowData?.surchargesList
                      ?.sort(applySubChargesSort)
                      .filter(s =>
                        accessorialDescriptions.includes(s.description)
                      )
                      .reduce((total, s) => total + (s.amount?.amount || 0), 0)
                      .toFixed(2)}
                  </TableCell>

                  {Object.keys(carrierAccessorialsEntry).length !== 0 &&
                  String(carrierAccessorials?.[1]).trim() &&
                  !['$-', 'null'].includes(
                    String(carrierAccessorials?.[1]).trim()
                  ) ? (
                    <>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {carrierAccessorials[0]}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatePrice(carrierAccessorials[1])}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <Typography
                          sx={{
                            color: isAlmostEqual(
                              rowData?.surchargesList
                                ?.sort(applySubChargesSort)
                                .filter(s =>
                                  accessorialDescriptions.includes(
                                    s.description
                                  )
                                )
                                .reduce(
                                  (total, s) => total + (s.amount?.amount || 0),
                                  0
                                )
                                .toFixed(2),
                              carrierAccessorials[1]
                            )
                              ? 'green'
                              : 'red',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          {isAlmostEqual(
                            rowData?.surchargesList
                              ?.sort(applySubChargesSort)
                              .filter(s =>
                                accessorialDescriptions.includes(s.description)
                              )
                              .reduce(
                                (total, s) => total + (s.amount?.amount || 0),
                                0
                              )
                              .toFixed(2),
                            carrierAccessorials[1]
                          )
                            ? '✅ Matched'
                            : '❌ Not Matched'}
                        </Typography>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                    </>
                  )}
                </TableRow>
              )}

              {/* Extra Rows */}
              {extraRows.length > 0 &&
                extraRows.map((extra, index) => (
                  <TableRow key={`extra-${index}`}>
                    <TableCell sx={{ fontWeight: 500 }}>_</TableCell>
                    <TableCell>_</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {extra.label}
                    </TableCell>
                    <TableCell>{extra.value}</TableCell>
                    <TableCell>_</TableCell>
                  </TableRow>
                ))}

              {/* Net Charge Row */}
              <TableRow
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 1,
                  backgroundColor: '#f5f5f5',
                  '&:last-child td, &:last-child th': {
                    border: 0
                  },
                  '&:hover': {
                    backgroundColor: '#f7f7f7'
                  }
                }}
              >
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>
                  Net Charge
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  $
                  {(
                    rowData?.shipmentCharges -
                    (rowData?.surchargesList?.find(
                      item => item.description === 'Surcharge'
                    )?.amount?.amount || 0)
                  ).toFixed(2)}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>
                  {rowData?.carrierName === 'R and L Carriers'
                    ? 'TotalCharge'
                    : netChargeObject[0]}
                </TableCell>

                <TableCell sx={{ fontWeight: 'bold' }}>
                  {String(netChargeObject[1]).includes('$')
                    ? netChargeObject[1]
                    : `$${Number(netChargeObject[1]).toFixed(2)}`}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Typography
                    sx={{
                      color: isAlmostEqual(
                        rowData?.shipmentCharges -
                          (rowData?.surchargesList?.find(
                            item => item.description === 'Surcharge'
                          )?.amount?.amount || 0),
                        netChargeObject[1]
                      )
                        ? 'green'
                        : 'red',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {isAlmostEqual(
                      rowData?.shipmentCharges -
                        (rowData?.surchargesList?.find(
                          item => item.description === 'Surcharge'
                        )?.amount?.amount || 0),
                      netChargeObject[1]
                    )
                      ? '✅ Matched'
                      : '❌ Not Matched'}
                  </Typography>

                  {!isAlmostEqual(
                    rowData?.shipmentCharges -
                      (rowData?.surchargesList?.find(
                        item => item.description === 'Surcharge'
                      )?.amount?.amount || 0),
                    netChargeObject[1]
                  ) &&
                    rowData.iaStatus === 'RESOLVED' && (
                      <Typography
                        sx={{
                          mt: 0.5,
                          color: 'green',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontWeight: 500
                        }}
                      >
                        ✔️ Resolved — Threshold Met
                      </Typography>
                    )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* History Drawer - Tabular View with Revisions as Rows */}
      <Drawer
        anchor='top'
        open={logsDrawerOpen}
        onClose={() => setLogsDrawerOpen(false)}
        PaperProps={{
          sx: { width: '100%', p: 2 }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            pb: 1,
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Typography
            variant='h6'
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <HistoryIcon sx={{ color: '#1976d2' }} /> Invoice Revision
            Comparison
          </Typography>
          {auditLogs.length !== 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* Export Buttons */}
              <Button
                variant='outlined'
                size='small'
                onClick={handleExportComparisonCSV}
                startIcon={<GetAppIcon />}
                sx={{ fontSize: '0.75rem' }}
              >
                Export CSV
              </Button>
              <IconButton
                onClick={() => setLogsDrawerOpen(false)}
                sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        </Box>

        {auditLogs.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              gap: 2
            }}
          >
            <HistoryIcon sx={{ fontSize: 48, color: '#bdbdbd' }} />
            <Typography color='textSecondary' align='center'>
              No revision history available
            </Typography>
          </Box>
        ) : (
          <div ref={comparisonTableRef}>
            <TableContainer
              component={Paper}
              sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}
            >
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow>
                    {/* First column: Revision with sort */}
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        minWidth: 140
                      }}
                    >
                      <TableSortLabel
                        active={sortConfig.field === 'revision'}
                        direction={
                          sortConfig.field === 'revision'
                            ? sortConfig.direction
                            : 'asc'
                        }
                        onClick={() => handleSort('revision')}
                      >
                        Revision
                      </TableSortLabel>
                    </TableCell>

                    {/* All field names as column headers with sort */}
                    {allFields.map((field, index) => (
                      <TableCell
                        key={field}
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                          minWidth: 150,
                          borderLeft: index > 0 ? '1px solid #e0e0e0' : 'none'
                        }}
                        align='center'
                      >
                        <TableSortLabel
                          active={sortConfig.field === field}
                          direction={
                            sortConfig.field === field
                              ? sortConfig.direction
                              : 'asc'
                          }
                          onClick={() => handleSort(field)}
                        >
                          {field}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* One row per selected revision - now using sortedRevisions */}
                  {sortedRevisions.map((revision, revIndex) => {
                    const log = auditLogs.find(
                      l => l.revisionNumber === revision
                    );

                    return (
                      <TableRow
                        key={revision}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      >
                        {/* Revision info cell - sticky on left */}
                        <TableCell
                          component='th'
                          scope='row'
                          sx={{
                            fontWeight: 'bold',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: 'white',
                            zIndex: 2,
                            borderRight: '2px solid #e0e0e0',
                            maxWidth: 140
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              gap: 0.5
                            }}
                          >
                            <Typography variant='subtitle2'>
                              Revision {revision}
                            </Typography>
                            <Typography variant='caption' color='textSecondary'>
                              {log ? formatDate(log.createdOn) : ''}
                            </Typography>
                            {log && (
                              <Chip
                                size='small'
                                label={log.iaStatus}
                                color={getStatusColor(log.iaStatus)}
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>

                        {/* Values for each field */}
                        {allFields.map((field, fieldIndex) => {
                          const fieldData = comparisonData.find(
                            row => row.field === field
                          );
                          const cellData = fieldData
                            ? fieldData[`revision_${revision}`]
                            : null;

                          // Check if this value changed from previous revision
                          const prevRevision = selectedRevisions[revIndex - 1];
                          const prevFieldData = prevRevision
                            ? comparisonData.find(row => row.field === field)
                            : null;
                          const prevCellData = prevFieldData
                            ? prevFieldData[`revision_${prevRevision}`]
                            : null;
                          const hasChanged =
                            prevCellData &&
                            cellData &&
                            prevCellData.value !== cellData.value;

                          return (
                            <TableCell
                              key={`${revision}-${field}`}
                              align='right'
                              sx={{
                                borderLeft:
                                  fieldIndex > 0 ? '1px solid #e0e0e0' : 'none',
                                backgroundColor: hasChanged
                                  ? '#fff3e0'
                                  : 'inherit',
                                fontWeight: hasChanged ? 'bold' : 'normal',
                                position: 'relative',
                                minWidth: 150
                              }}
                            >
                              {cellData?.value !== '-' && (
                                <Tooltip
                                  title={`Updated: ${cellData?.date ? formatDate(cellData.date) : ''}`}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                      gap: 0.5
                                    }}
                                  >
                                    {/* {hasChanged && (
                                      <ChangeCircleIcon
                                        sx={{
                                          fontSize: '1rem',
                                          color: '#ff9800',
                                          position: 'absolute',
                                          left: 2
                                        }}
                                      />
                                    )} */}
                                    <span>{cellData?.value}</span>
                                  </Box>
                                </Tooltip>
                              )}
                              {cellData?.value === '-' && '-'}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </Drawer>
    </Box>
  );
}

// Add requiredDescriptions array since it's missing from the original code
const requiredDescriptions = [
  'Base Rate',
  'Discount %',
  'Discount Value',
  'Line Haul',
  'Fuel Surcharge %',
  'Fuel Surcharge Value'
];
